import React, { useState } from 'react';
import { DateTime } from 'luxon';
import { MealType } from '../../common/commonTypes';
import { IconButton } from '@mui/material';
import Paper from '@mui/material/Paper';
import DeleteIcon from '@mui/icons-material/Delete';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';

import MoreVertIcon from '@mui/icons-material/MoreVert';

import MacroBullet from '../MacroBullet';
import MacroBar from '../MacroBar';
import { useTranslation } from 'react-i18next';

const options = ['None', 'Atria', 'Callisto'];

const ITEM_HEIGHT = 48;

type MealCardType = {
    meal: MealType;
    onDelete: (meal: MealType) => void;
    onLog: (meal: MealType) => void;
    onLogSome?: (meal: MealType) => void; // this FN is for Favorites Tab
    onEdit: (meal: MealType) => void; // this FN is for Favorites or History Tabs
    onFav?: (meal: MealType) => void; // this FN is for History Tab
};

const MealCard = (props: MealCardType) => {
    const { meal, onDelete, onLog, onLogSome, onEdit, onFav } = props;

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const { t, i18n } = useTranslation();

    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <Paper className="p-1 mb-4" elevation={2}>
            <div className="my-2 ml-2 flex justify-between">
                <h4 className="text-gray-600 text-sm">
                    {/* for History Tab */}
                    {Boolean(onFav) && (
                        <>
                            <strong>
                                {DateTime.fromISO(meal.createdAt as string)
                                    .setLocale(i18n.language)
                                    .toFormat('t')}
                            </strong>{' '}
                            - {meal.name}
                        </>
                    )}

                    {/* for Favorites Tab */}
                    {!Boolean(onFav) && (
                        <>
                            <strong>{meal.name}</strong>
                            <br />
                            <small>
                                <strong>{t('created_at')}: </strong>
                                {DateTime.fromISO(meal.createdAt as string)
                                    .setLocale(i18n.language)
                                    .toFormat('DD')}
                            </small>
                        </>
                    )}
                </h4>

                <IconButton onClick={handleClick}>
                    <MoreVertIcon />
                </IconButton>
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem
                        onClick={() => {
                            handleClose();
                            onDelete(meal);
                        }}
                    >
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" />
                        </ListItemIcon>
                        <span className="text-sm">{t('MEAL_MENU_delete')}</span>
                    </MenuItem>

                    {Boolean(onEdit) && (
                        <MenuItem
                            onClick={() => {
                                handleClose();
                                // @ts-ignore
                                onEdit(meal);
                            }}
                        >
                            <ListItemIcon>
                                <EditIcon fontSize="small" />
                            </ListItemIcon>
                            <span className="text-sm">{t('MEAL_MENU_edit')}</span>
                        </MenuItem>
                    )}

                    {Boolean(onFav) && (
                        <>
                            <MenuItem
                                onClick={() => {
                                    handleClose();
                                    // @ts-ignore
                                    onFav(meal);
                                }}
                            >
                                <ListItemIcon>
                                    <FavoriteIcon fontSize="small" />
                                </ListItemIcon>
                                <span className="text-sm">{t('MEAL_MENU_add_to_fav')}</span>
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    handleClose();
                                    // @ts-ignore
                                    onLog(meal);
                                }}
                            >
                                <ListItemIcon>
                                    <SendIcon fontSize="small" />
                                </ListItemIcon>
                                <span className="text-sm">{t('MEAL_MENU_log_again')}</span>
                            </MenuItem>
                        </>
                    )}

                    {!Boolean(onFav) && (
                        <MenuItem
                            onClick={() => {
                                handleClose();
                                // @ts-ignore
                                onLogSome(meal);
                            }}
                        >
                            <ListItemIcon>
                                <SendOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <span className="text-sm">{t('MEAL_MENU_log_part')}</span>
                        </MenuItem>
                    )}
                </Menu>
            </div>

            <div className="flex justify-between">
                <table className="text-xs ml-6">
                    <tbody>
                        <tr>
                            <td>
                                <div className="flex items-center pr-4">
                                    <b>{t('calories')}:&nbsp;</b> {meal.calories} {t('cal')}
                                </div>
                            </td>
                            <td>
                                <div className="flex items-center">
                                    <b>{t('weight')}:&nbsp;</b> {meal.weight} {t('gr')}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <div className="flex items-center pr-4">
                                    <MacroBullet type="protein" />
                                    <b>{t('protein')}:&nbsp;</b> {meal.protein} {t('gr')}
                                </div>
                            </td>
                            <td>
                                <div className="flex items-center">
                                    <MacroBullet type="fat" />
                                    <b>{t('fat')}:&nbsp;</b> {meal.fats} {t('gr')}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <div className="flex items-center pr-4">
                                    <MacroBullet type="carb" />
                                    <b>{t('carbs')}:&nbsp;</b> {meal.carbsNoSugars} {t('gr')}
                                </div>
                            </td>
                            <td>
                                <div className="flex items-center">
                                    <MacroBullet type="sugar" />
                                    <b>{t('sugar')}:&nbsp;</b> {meal.sugars} {t('gr')}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* for Favorites Tab */}
                {!Boolean(onFav) && (
                    <IconButton
                        aria-label="log meal"
                        color="primary"
                        size="small"
                        onClick={(e) => {
                            onLog(meal);
                        }}
                    >
                        <SendIcon />
                    </IconButton>
                )}
            </div>

            <div className="px-2">
                <MacroBar
                    protein={meal.protein}
                    fat={meal.fats}
                    carb={meal.carbsNoSugars}
                    sugar={meal.sugars}
                />
            </div>
        </Paper>
    );
};

export default MealCard;
